import { ProposalSpikedTireSeasonBanner } from '@components/BookingFlow/Common/MobileBookingSpikedTireSeasonBanner';
import { SelectedTimeWindow } from '@components/BookingFlow/Mobile/SelectedTimeWindow';
import { TimeWindowModal } from '@components/BookingFlow/Mobile/TimeWindows/TimeWindowModal';
import { CustomerTimePickerNotices } from '@components/Common/CustomerTimePickerNotices';
import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { FlowBackButton } from '@components/Layouts/FlowBackButton';
import { SelectionBottomBar } from '@components/Layouts/SelectionBottomBar';
import { useShowsSelectionBottomBar } from '@components/Layouts/useShowsSelectionBottomBar';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { LoadingScreen, NoddiButton } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { BookingTimeWindowPicker, commonTrackingEvents, tracking, useAuthContext } from 'noddi-web';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrackProposalStep } from '../../../hooks/useTrackProposalStep';
import {
  useBookingProposalActions,
  useBookingProposalAddress,
  useBookingProposalAnalytics,
  useBookingProposalItems,
  useBookingProposalSlug,
  useBookingProposalTimeWindow
} from '../../../stores/bookingProposalStore';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { getCommonTranslations } from '../../../utils/commonTranslations';

export const BookingProposalTimeWindowPicker = () => {
  const slug = useBookingProposalSlug();
  const analytics = useBookingProposalAnalytics();
  useTrackProposalStep('timePicker');
  const { currentBrand } = useCurrentBrand();
  const {
    data: bookingProposal,
    isPending: isBookingProposalPending,
    error: bookingProposalError
  } = noddiAsync.useGet({
    type: URLKeys.getBookingProposalBySlug,
    input: { slug }
  });

  const address = useBookingProposalAddress();
  const bookingProposalItems = useBookingProposalItems();
  const { isSuperUser } = useAuthContext();

  const [showExtraInfoModal, setShowExtraInfoModal] = useState(false);

  const bookingTimeWindow = useBookingProposalTimeWindow();
  const showsBottomBar = useShowsSelectionBottomBar(!!bookingTimeWindow);

  const { setBookingTimeWindow } = useBookingProposalActions();
  const navigate = useNavigate();

  if (isBookingProposalPending) {
    return <LoadingScreen />;
  }

  if (bookingProposalError) {
    return <ApiErrorPageWithTrans error={bookingProposalError} />;
  }

  // Use dateSuggested as the initial starting date for the timepicker
  // The timepicker will automatically prioritize any existing selected delivery window
  const initialFromDate = bookingProposal?.dateSuggested ?? undefined;

  return (
    <div>
      <ContentWrapperCustomerApp
        width='lg'
        title={t`When is it convenient for us to come to you?`}
        className='mb-24'
        hideBackButton={showsBottomBar}
      >
        <TimeWindowModal open={showExtraInfoModal} onClose={() => setShowExtraInfoModal(false)} />
        <ProposalSpikedTireSeasonBanner />
        <CustomerTimePickerNotices
          surface='mobile'
          addressId={address?.id}
          selectedSalesItemIds={bookingProposalItems.flatMap((item) =>
            item.salesItemsWithQuantity.map((salesItem) => salesItem.salesItem.id)
          )}
        />
        <BookingTimeWindowPicker
          address={address!}
          brandDomain={currentBrand}
          surface='page'
          cars={bookingProposalItems.map((bookingProposalItem) => ({
            licensePlate: bookingProposalItem.car.licensePlate,
            selectedSalesItemIds: bookingProposalItem.salesItemsWithQuantity.map((salesItem) => salesItem.salesItem.id)
          }))}
          selectedDeliveryWindow={bookingTimeWindow}
          initialFromDate={initialFromDate}
          onAvailableSelect={(timeWindow) => {
            setBookingTimeWindow(timeWindow);
          }}
          translations={getCommonTranslations().timePicker}
          onUnavailableSelect={(timeWindow) => {
            if (!isSuperUser) {
              return;
            }
            setBookingTimeWindow(timeWindow);
          }}
          customButton={
            <NoddiButton
              className='h-9 md:h-8'
              size='small'
              startIcon='Info'
              onClick={() => {
                setShowExtraInfoModal(true);
                tracking.track('clickedReadMoreAboutDeliveryTimePicker');
              }}
              variant='outline'
            >
              <Trans>About delivery</Trans>
            </NoddiButton>
          }
        />
      </ContentWrapperCustomerApp>

      <SelectionBottomBar hasSelection={!!bookingTimeWindow}>
        <div className='flex items-center gap-2'>
          <FlowBackButton />
          <SelectedTimeWindow selectedTimeWindow={bookingTimeWindow} />
        </div>
        <div className='flex w-full justify-end'>
          <NoddiButton
            disabled={!bookingTimeWindow}
            endIcon='ArrowRight'
            onClick={() => {
              tracking.track(commonTrackingEvents.proposalTimeSelected, { ...analytics, method: 'picker' });
              navigate(customerRoutes.bookingProposalConfirmation.getPath({ slug }));
            }}
          >
            <Trans>Next</Trans>
          </NoddiButton>
        </div>
      </SelectionBottomBar>
    </div>
  );
};
